import React, { useState, useEffect } from "react";

import Box from '@mui/material/Box';

import "../App.css"
import {useAppCtx} from "../app/AppCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {PageHeader} from "./PageHeader";
import {accessCtrl} from "../ctx/AccessCtrl";
import {CxDataGrid} from "../cmx/CxData";
import {CxGrid} from "../cmx/CxGrid";
import {CxFormFeedback, CxSnack} from "../cmx/CxFeedback";
import {CxSubtitle, CxText} from "../cmx/CxTypography"
import {Project} from "../domain/Project";
import {initialCall, runningCall, successCall} from "../gateway/CallService";
import {projectSearchList} from "../gateway/ProjectService"
import {
    CxResetSearchButton,
    CxComboxSelection,
    CxDatePicker,
    CxTextField, CxLocalization, CxRadioboxSelection
} from "../cmx/CxForm";
import {DateUtil} from "../lib/DateUtil";
import {ValidationUtil} from "../lib/ValidationUtil";


export function ProjectSearch() {

    const {handleAction, getPageData, setPageData, getPageState, setPageState} = useAppCtx();
    const {crew, lazyloadCrews} = useCrewCtx();

    const pageId  = 'ProjectSearch';        // id used for PageState and PageData
    const viewKey = 'view';                 // PageState
    const dataKey = 'searchCriteria';       // PageData

    useEffect(() => {
        lazyloadCrews();
    }, []);

    // ----------------------------------------------------------------------------------------------------------------
    // header and view handling
    // ----------------------------------------------------------------------------------------------------------------

    const viewConfig = {
        currentView: {
            title     : "Projekte suchen",
            desc      : "Geben Sie die Suchkriterien ein die alle gemeinsam zutreffen sollen. Beachte: die Suche ist auf max. 100 Treffer limitiert.",
            isCurrent : true
        },
        archiveView: {
            title     : "Archivierte Projekte suchen",
            desc      : "Geben Sie die Suchkriterien ein die alle gemeinsam zutreffen sollen. Beachte: die Suche ist auf max. 100 Treffer limitiert.",
            isCurrent : false
        }
    };

    const view = getPageState(pageId, viewKey);
    const cfg  = viewConfig[view];

    const handleViewChange = (value) => {
        setPageState(pageId, viewKey, value);
        setCall(initialCall());
    };

    const viewItems = [
        { label: "aktuelle",    value: "currentView" },
        { label: "archivierte", value: "archiveView" }
    ];

    const actionItems = [
        { kind: "tooltip", label: "Zurück zu Projekte", iconName: "project", actionName: "project" }
    ];

    const renderHeader = () => {
        return (
            <>
                <PageHeader
                    title={cfg.title}
                    viewItems={viewItems}
                    viewInitialValue={view}
                    viewOnChangeHandler={handleViewChange}
                    actionItems={actionItems}
                />
                <CxGrid size="xl">
                    <CxText>{cfg.desc}</CxText>
                </CxGrid>
            </>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // control panel
    // ----------------------------------------------------------------------------------------------------------------

    const [formValues, setFormValues] = useState( getPageData(pageId, dataKey, Project.getSearchValues()) );
    const [formErrors, setFormErrors] = useState( Project.getSearchErrors() );
    const [call, setCall] = useState(initialCall());
    const [snack, setSnack] = useState(null);


    //
    // handers
    //
    const handleChange = (fieldOrEvent, value) => {
        if (typeof fieldOrEvent === "string") {
            // For DatePicker
            setFormValues((prev) => ({ ...prev, [fieldOrEvent]: DateUtil.convertDateToIsoString(value) }));
            setFormErrors((prev) => ({ ...prev, [fieldOrEvent]: false }));
        } else {
            // For TextField or CheckBox
            const { name, value, type, checked} = fieldOrEvent.target;
            if (type === "checkbox") {
                // For CheckBox: Convert checked state to 0/1
                setFormValues((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
            } else {
                // For TextField: Sanitize and update value
                setFormValues((prev) => ({ ...prev, [name]: value }));
            }
            setFormErrors((prev) => ({ ...prev, [name]: false }));
        }
    };

    const handleComboxChange = (field, value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            [field]: value
        }));
    }

    const handleClosedSourceChange = (value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            closed: value,
        }));
        formErrors.closed = "";
    }

    const handleSubmit = async (action) => {

        if (action === "reset") {
            setFormValues( Project.getSearchValues() )
            setPageData(pageId, dataKey, Project.getSearchValues() );
            setFormErrors( Project.getSearchErrors() )
            setCall(initialCall());
            return
        }

        if (action === "search") {
            // validate
            const errorsCopy = { ...formErrors }; // Create a copy of the errors object
            const isValid = validate(formValues, errorsCopy);
            if (!isValid) {
                setFormErrors(errorsCopy); // Update errors in state
                return
            }
            // save search data
            setPageData(pageId, dataKey, formValues);
            // execute search
            search( Project.mapSearchToQuery(formValues) );
        }
    };

    //
    // validation
    //
    function validate(formValues, formErrors) {
        let isValid = true;

        // remove leading and trailing blanks on all input fields
        ValidationUtil.trimTextFields(formValues)

        // check if all search fields are empty
        if ( ValidationUtil.areEqual(formValues, Project.getSearchValues()) ) {
            const snack= `Bitte mindestens 1 Suchkriterium eingeben.`;
            setSnack(snack)
            return false;
        }

        // validate minimal lenth
        isValid = ValidationUtil.minLength(formValues, formErrors, Project.getSearchMinimalLength());

        // validate date range
        if (! ValidationUtil.dateRange(formValues, formErrors, 'start_initial_date', 'end_initial_date') ){
            isValid = false;
        }

        // validate avor amount von/bis
        if (! ValidationUtil.numberRange(formValues, formErrors, 'avor_amount_from', 'avor_amount_to') ){
            isValid = false;
        }


        // return final validation status
        return isValid;
    }

    //
    // form inputs
    //
    const formTextField = (field, label, maxLength, numericOnly=false) => {
        return <CxTextField field={field} label={label} maxLength={maxLength} numericOnly={numericOnly}
                            handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formDatePicker = (field, label) => {
        return <CxDatePicker field={field} label={label} disablePast={false}
                             handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCombox = (field, label, items) => {
        return <CxComboxSelection id={field} name={label} value={formValues[field]}
                                  onChange={(value) => handleComboxChange(field, value)}
                                  items={items} formErrors={formErrors} />
    }

    const formClosedSelection = () => {
        return <CxRadioboxSelection
            key={formValues['closed']}
            name='closed'
            label="Abgeschlossen"
            value={formValues['closed']}
            items={Project.getSearchClosedItems(true)}
            onChange={handleClosedSourceChange}  />
    }

    const formResetSearchButton = () => {
        return <CxResetSearchButton handleSubmit={handleSubmit} />
    }

    const renderCtrl = () => {
        return (

            <CxLocalization>

                <CxGrid size="lg">{ formTextField("project_ref", "Projekt Nr", 64) }</CxGrid>
                <CxGrid size="lg">{ formTextField("architect", "Auftraggeber", 64) }</CxGrid>

                <CxGrid size="lg">{ formDatePicker("start_initial_date", "Projektstart ab")}</CxGrid>
                <CxGrid size="lg">{ formDatePicker("end_initial_date", "Projektstart bis")}</CxGrid>

                <CxGrid size="lg">{ formTextField("address", "Adresse", 64) }</CxGrid>
                <CxGrid size="sm">{ formTextField("zip", "PLZ", 32) }</CxGrid>
                <CxGrid size="sm">{ formTextField("city", "Ort", 32) }</CxGrid>

                <CxGrid size="sm" container spacing={2}>
                    <CxGrid size="lg">{ formTextField("avor_amount_from", "Auftragssumme von", 8, true) }</CxGrid>
                    <CxGrid size="lg">{ formTextField("avor_amount_to", "Auftragssumme bis", 8, true) }</CxGrid>
                </CxGrid>
                <CxGrid size="sm">{ formCombox("avor_state", "Vorausmass", Project.getAvorStateItems()) }</CxGrid>
                <CxGrid size="sm">{ formCombox("avor_created_crew_id", "Vorausmass erstellt von", crew.getAvorSelection(true)) }</CxGrid>
                <CxGrid size="sm">{ formDatePicker("avor_created_at", "Vorausmass erstellt am") }</CxGrid>

                <CxGrid size="sm">{ formCombox("foreman_crew_id", "Bauführer", crew.getForemanSelection()) }</CxGrid>
                <CxGrid size="sm">{ formCombox("deputy_foreman_crew_id", "Bauführer Stellvertreter", crew.getForemanSelection()) }</CxGrid>
                <CxGrid size="lg">{ formClosedSelection() }</CxGrid>

                <CxGrid size="xl" justifyContent="flex-end">{ formResetSearchButton() }</CxGrid>

                <CxSnack message={snack} handleClose={() => setSnack(null)} position="topCenter" />

            </CxLocalization>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // select view data
    // ----------------------------------------------------------------------------------------------------------------

    function search(queryParams) {
        setCall(runningCall())
        projectSearchList(queryParams, cfg.isCurrent).then(responseCall => {
            if (responseCall.isSuccess) {
                const mappedRows = Project.mapListColumns(responseCall.result, crew);
                setCall(successCall(mappedRows));
            } else {
                setCall(responseCall);
            }
        });
    }

    // ----------------------------------------------------------------------------------------------------------------
    // render view
    // ----------------------------------------------------------------------------------------------------------------

    const handleRowDoubleClick = (params) => {
        if (cfg.isCurrent) {
            handleAction("projectDetail", params.row, "projectSearch");     // return back to 'projectSearch' after edit
        } else {
            handleAction("archiveDetail", params.row);
        }
    };

    const handleEditClick = (params) => {
        handleAction("projectUpdate", params.row, "projectSearch");     // return back to 'projectSearch' after edit
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>

                { renderHeader() }
                { renderCtrl() }

                <CxFormFeedback call={call} />

                { call.isSuccess &&
                    <>
                        <CxSubtitle>Resultate</CxSubtitle>
                        <CxGrid size="xl">
                            <CxDataGrid
                                rows={call.result}
                                columns={Project.getListColumns(true, false)}
                                onRowDoubleClick={handleRowDoubleClick}
                                onViewAction={handleRowDoubleClick}
                                onEditAction={cfg.isCurrent && accessCtrl.canEditAllProject ? handleEditClick : undefined}
                                noRecordsMessage="Keine Suchresultate gefunden."
                            />
                        </CxGrid>
                    </>
                }

            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.